import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';

import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { BatchStates } from '../../utils/enums/BatchStates';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { Roles } from '../../utils/enums/Roles';
import { IConnectProps } from '../../utils/IConnectProps';
import { IRouterProps } from '../../utils/IRouterProps';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelDatePicker,
    EditPanelFilterMultiSelect,
    EditPanelInput,
    EditPanelLabel,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    PanelHeader,
    Tray,
} from '../elements';
import { TopBar } from './Common/TopBar';

interface IBatchAddEditProps extends ITranslateProps, IConnectProps, IRouterProps, ILanguageCodeProps {
    userRole: string;
}

interface IValueText {
    value: string;
    text: string;
}

interface IBatchAddEditState extends IValidationComponentState {
    clientCompanies: IValueText[];
    executorCompaniesToSelect: IValueText[];
    name?: string;
    status?: BatchStates;
    inspectionStandardType: string;
    clientCompanyId?: string;
    executorCompanies: string[];
    plannedStartDate?: string | null;
    plannedEndDate?: string | null;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        userRole: authentication.role,
        languageCode: language.code,
    };
}

class BatchAddEdit extends ValidatingComponent<IBatchAddEditProps, IBatchAddEditState> {
    constructor(props: IBatchAddEditProps) {
        super(props);

        this.state = {
            status: BatchStates.Open,
            clientCompanies: [],
            executorCompaniesToSelect: [],
            executorCompanies: [],
            inspectionStandardType: InspectionStandardTypes.Fmeca,
            errors: {},
        };
    }

    public override async componentDidMount() {
        if (this.props.match.params.id) {
            // edit object
            await this.load();
        } else {
            // new object
            if (this.props.userRole === Roles.Administrator) {
                await this.loadClientCompanies();
            } else {
                await this.loadCompanies();
            }
        }
    }

    public getValidatorTypes(): any {
        const schema = {
            name: Joi.string().required().label(this.props.t('common.name')),
        };
        if (this.props.userRole === Roles.Administrator) {
            return { ...schema, clientCompanyId: Joi.string().required().label(this.props.t('common.client')) };
        }
        return schema;
    }

    public getValidatorData() {
        return {
            name: this.state.name,
            status: this.state.status,
            inspectionStandardType: this.state.inspectionStandardType,
            clientCompanyId: this.state.clientCompanyId,
            executorCompanies: this.state.executorCompanies,
            plannedStartDate: this.state.plannedStartDate,
            plannedEndDate: this.state.plannedEndDate,
        };
    }

    public override render() {
        return (
            <>
                <TopBar batchId={this.props.match.params.id} activeTab="addEdit" />
                <Content>
                    <Tray>
                        {this.renderMessage()}
                        <div className="panel-heading mw800 center-block"></div>
                        <div className="admin-form theme-primary mw800 center-block">
                            <div className="panel">
                                <form
                                    method="post"
                                    id="admin-form"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        void this.saveClicked();
                                    }}
                                >
                                    <div className="panel-body bg-light">
                                        {/* Name */}
                                        <EditPanelSection>
                                            <EditPanelInput
                                                name="name"
                                                placeholder={this.props.t('common.name')}
                                                value={this.state.name}
                                                icon="fa fa-briefcase"
                                                handleChange={this.handleChange.bind(this)}
                                                notValid={!this.isValid('name')}
                                                handleBlur={() => {
                                                    this.handleValidation();
                                                }}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('name'))}
                                        </EditPanelSection>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="status"
                                                    value={this.state.status}
                                                    handleChange={this.handleChange.bind(this)}
                                                    options={this.batchStates()}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="inspectionStandardType"
                                                    value={this.state.inspectionStandardType}
                                                    handleChange={this.handleChange.bind(this)}
                                                    options={getInspectionStandardTypeOptions(this.props.t)}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>

                                        {/* Only a administrator can select a client */}
                                        {this.props.userRole === Roles.Administrator && this.state.clientCompanies.length > 1 && (
                                            <EditPanelRow>
                                                <EditPanelLabel rowCount={3}>{this.props.t('common.client')}</EditPanelLabel>
                                                <EditPanelCell rowCount={9}>
                                                    <EditPanelSelect
                                                        name="clientCompanyId"
                                                        value={this.state.clientCompanyId}
                                                        handleChange={this.handleChange.bind(this)}
                                                        disabled={this.state.submitting}
                                                        placeholder={this.props.t('common.selectItem', this.props.t('common.client'))}
                                                        options={this.state.clientCompanies}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        )}

                                        {/* Select surveyor */}
                                        {this.state.executorCompaniesToSelect.length > 0 && (
                                            <EditPanelRow>
                                                <EditPanelLabel rowCount={3}>{this.props.t('batch.executor')}</EditPanelLabel>
                                                <EditPanelCell rowCount={9}>
                                                    <EditPanelFilterMultiSelect
                                                        name="executorCompanies"
                                                        value={this.state.executorCompanies}
                                                        handleChange={this.handleChange.bind(this)}
                                                        disabled={this.state.submitting}
                                                        placeholder={this.props.t('common.selectItem', this.props.t('batch.executor'))}
                                                        options={this.state.executorCompaniesToSelect}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        )}
                                        <PanelHeader text={this.props.t('batch.plannedDate')} />
                                        <EditPanelRow>
                                            <EditPanelLabel rowCount={1}>{this.props.t('common.start')}</EditPanelLabel>
                                            <EditPanelCell rowCount={3}>
                                                <EditPanelDatePicker
                                                    name="plannedStartDate"
                                                    value={this.state.plannedStartDate}
                                                    handleChange={this.handleChange.bind(this)}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={1}>&nbsp;</EditPanelCell>
                                            <EditPanelLabel rowCount={1}>{this.props.t('common.end')}</EditPanelLabel>
                                            <EditPanelCell rowCount={3}>
                                                <EditPanelDatePicker
                                                    name="plannedEndDate"
                                                    value={this.state.plannedEndDate}
                                                    handleChange={this.handleChange.bind(this)}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </div>

                                    {/* <div className="panel-footer text-right"> */}
                                    <EditPanelButtons footer={true}>
                                        <EditPanelButton
                                            onClick={() => this.props.navigate('/Batches')}
                                            text={this.props.t('common.cancel')}
                                            small={false}
                                            disabled={this.state.submitting}
                                        />
                                        <EditPanelButton
                                            onClick={() => this.saveClicked()}
                                            text={this.props.t('common.save')}
                                            small={false}
                                            primary={true}
                                            disabled={this.state.submitting}
                                        />
                                    </EditPanelButtons>
                                </form>
                            </div>
                        </div>
                    </Tray>
                </Content>
            </>
        );
    }

    protected override async onChanged(value: any, stateKey: string) {
        if (stateKey === 'clientCompanyId') {
            if (value && value !== '') {
                await this.loadCompanies(value);
            } else {
                await this.setStatePromised({
                    executorCompaniesToSelect: [],
                    executorCompanies: [],
                });
            }
        }
    }

    private async loadValueText(url: string, stateKey: string) {
        const result = await fetchGet(url, {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json.items ? result.json.items : result.json;

        const keyValueArray = items.map((item: any) => ({ value: item.id, text: item.name }));
        const newState = {} as any;
        newState[stateKey] = keyValueArray;
        await this.setStatePromised(newState);
    }

    private async loadCompanies(clientCompanyId?: string) {
        if (clientCompanyId) {
            await this.loadValueText(`/api/companyList?type=surveyor|operator&companyid=${clientCompanyId}`, 'executorCompaniesToSelect');
        } else {
            await this.loadValueText('/api/companyList?type=surveyor|operator', 'executorCompaniesToSelect');
        }
    }

    private async loadClientCompanies() {
        await this.loadValueText('/api/clients', 'clientCompanies');

        if (this.props.location.query.clientCompanyId) {
            await this.handleChange(this.props.location.query.clientCompanyId, 'clientCompanyId');
        }
        if (this.state.clientCompanies.length === 1) {
            await this.handleChange(this.state.clientCompanies[0]!.value, 'clientCompanyId');
        }
    }

    private async load() {
        const url = `/api/batches/${this.props.match.params.id}`;
        const result = await fetchGet(url, {}, ['NO_DATA_AVAILABLE'], [404]);

        await this.setStatePromised({ ...result.json, clientCompanyId: result.json.clientCompany.id });
        await this.setStatePromised({ executorCompanies: result.json.executorCompanies.map((i: { company: { id: string } }) => i.company.id) });
        if (this.props.userRole === Roles.Administrator) {
            await this.loadClientCompanies();
            await this.loadCompanies(result.json.clientCompany.id);
        } else {
            await this.loadCompanies();
        }
    }

    private batchStates() {
        const options = [
            { value: BatchStates.Proposal, text: this.props.t('batchStatus.proposal') },
            { value: BatchStates.Open, text: this.props.t('batchStatus.open') },
            { value: BatchStates.Active, text: this.props.t('batchStatus.active') },
            { value: BatchStates.Completed, text: this.props.t('batchStatus.completed') },
        ];
        // administrator can edit batch to status deleted
        if (this.props.userRole === Roles.Administrator && this.props.match.params.id) {
            options.push({ value: BatchStates.Deleted, text: this.props.t('batchStatus.deleted') });
        }
        return options;
    }

    private saveClicked() {
        this.validate(async (validationError: any) => {
            if (validationError) {
                console.log(validationError);
            } else {
                await this.setStatePromised({ message: undefined, submitting: true, errorCode: undefined, failed: false });
                const data = this.getValidatorData() as any;

                let url = '/api/batches';
                if (this.props.match.params.id) {
                    url += `/${this.props.match.params.id}`;
                }

                const result = await fetchPost(url, {}, data, ['NO CLIENT']);

                if (result.errorCode === 'NO CLIENT') {
                    await this.setStatePromised({ submitting: false, errorCode: result.errorCode, failed: true });
                } else {
                    this.props.navigate('/batches');
                }
            }
        });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(BatchAddEdit as any) as any);
